import { UIHeading5 } from '@cian/ui-kit';
import { IconCommunicationPhone16 } from '@cian/ui-kit-design-tokens/icons';

import { type FC, useCallback, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import { Button } from 'shared/components/Button';
import { DEVELOPER_ID } from 'shared/constants/developer';
import { getFilterLocation } from 'shared/selectors/filters/getFilterLocation';
import { currentLocationByFiltersSelector } from 'shared/selectors/locations/currentLocationByFiltersSelector';
import { getPhone } from 'shared/selectors/phone/getPhone';
import { getUser } from 'shared/selectors/user/getUser';
import { getUserPhone } from 'shared/selectors/user/getUserPhone';
import { type ECallbackPlace } from 'shared/types/callback';
import { createGaUser, createLabel } from 'shared/utils/analytics';

import { NewbuildingCallbackWidgetContext } from '../NewbuildingCallbackWidgetProvider';

import { getUserIdByLocation } from './utils/getUserIdByLocation';

interface ICallbackProps {
  place: ECallbackPlace;
}

export const Callback: FC<ICallbackProps> = ({ place }) => {
  const [isLoading, setLoading] = useState(false);
  const location = useSelector(getFilterLocation);
  const userPhoneNumber = useSelector(getUserPhone);
  const user = useSelector(getUser);
  const agentPhone = useSelector(getPhone);

  const currentLocation = useSelector(currentLocationByFiltersSelector);
  const builderRealtyUserId = getUserIdByLocation(location);

  const openWidget = useContext(NewbuildingCallbackWidgetContext);

  const handlePopupOpen = useCallback(async () => {
    if (!agentPhone || !builderRealtyUserId) {
      return;
    }

    setLoading(true);

    await openWidget({
      agentPhone,
      builderRealtyUserId,
      theme: 'pik',
      timezoneOffset: currentLocation.timezoneOffset,
      trackingParams: {
        category: 'CardBuilder',
        gaLabel: createLabel(DEVELOPER_ID),
        opener: place,
        user: createGaUser(user),
      },
      userPhoneNumber,
    });

    setLoading(false);
  }, [openWidget, agentPhone, builderRealtyUserId, currentLocation.timezoneOffset, userPhoneNumber, user, place]);

  if (!agentPhone || !builderRealtyUserId) {
    return null;
  }

  return (
    <Button
      disabled={isLoading}
      icon={<IconCommunicationPhone16 color="icon-inverted-default" />}
      size="small"
      testId="CallbackFormButton"
      onClick={handlePopupOpen}
    >
      <UIHeading5 color="text-inverted-default">Обратный звонок</UIHeading5>
    </Button>
  );
};
